import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  LayoutDashboard, Activity, Sparkles, LogOut, Boxes, Layers, ShoppingBag, GitBranch, Users, CreditCard, Zap, Shield, Package, MessageCircle, Megaphone, GraduationCap, Menu, X, Calculator, CalendarDays, Trophy, Wrench, ScrollText, Palette, Puzzle, IdCard, Users2, Target, Lightbulb, Clock as ClockIcon, Wallet, HeartPulse, Moon, Sun, FileCheck2, FileSpreadsheet, Truck, CalendarRange,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../context/I18nContext";
import { useTheme } from "../../context/ThemeContext";
import { useBranding } from "../../context/BrandingContext";
import { canAccess, isPathAllowedForRD } from "../../lib/permissions";
import { api, mediaUrl } from "../../lib/api";
import NotificationBell from "./NotificationBell";
import GlobalSearch from "./GlobalSearch";
import ChatBell from "./ChatBell";
import TenantSwitcher from "./TenantSwitcher";
import CatalogNav from "./CatalogNav";
import DayStart from "./DayStart";

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button data-testid="os-theme-toggle" onClick={toggle} title={theme === "dark" ? "Modo claro" : "Modo oscuro"}
      className="w-9 h-9 rounded-full border border-navy/15 flex items-center justify-center text-navy/60 hover:text-navy hover:border-violet transition-colors">
      {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}

function LangToggle() {
  const { lang, setLang } = useI18n();
  return (
    <div data-testid="os-lang-toggle" className="flex items-center rounded-full border border-navy/15 overflow-hidden text-xs">
      {["es", "en"].map((l) => (
        <button key={l} data-testid={`os-lang-${l}`} onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 transition-colors ${lang === l ? "bg-navy text-cream" : "text-navy/50 hover:text-navy"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function BrandMark({ onClick, tag }) {
  const { branding } = useBranding();
  const logo = branding?.logo_url ? mediaUrl(branding.logo_url) : null;
  if (logo) {
    return (
      <Link to="/" onClick={onClick} className="flex items-center min-w-0" data-testid="brand-logo">
        <img src={logo} alt="Logo" className="h-9 max-w-[170px] object-contain" />
      </Link>
    );
  }
  return (
    <Link to="/" onClick={onClick} className="flex items-center min-w-0" data-testid="brand-wordmark">
      <span className="font-sans text-lg tracking-[0.25em]">OMNIA</span>
      {tag && <span className="ml-2 overline text-cream/30">{tag}</span>}
    </Link>
  );
}

export default function OSLayout({ children, title }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [enabledModules, setEnabledModules] = useState(null);

  useEffect(() => {
    api.get("/tenants/settings")
      .then((r) => setEnabledModules(Array.isArray(r.data?.enabled_modules) ? r.data.enabled_modules : null))
      .catch(() => {});
  }, []);

  const PROTECTED_NAV = new Set(["hub", "admin", "bitacora", "design", "workflows", "copiloto", "administracion", "marketplace", "extensiones", "integraciones", "plataforma", "assistant", "intelligence"]);

  const ALL_NAV = [
    { to: "/os", key: "hub", label: t("os.nav.hub"), icon: LayoutDashboard, exact: true },
    { to: "/os/gout", key: "gout", label: t("os.nav.gout"), icon: Layers },
    { to: "/os/tagma", key: "tagma", label: t("os.nav.tagma"), icon: Megaphone },
    { to: "/os/aura", key: "aura", label: t("os.nav.aura"), icon: ShoppingBag },
    { to: "/os/sheets", key: "sheets", label: "Documents Hub", icon: FileSpreadsheet, always: true },
    { to: "/os/flotilla", key: "flotilla", label: "Flotilla", icon: Truck, always: true },
    { to: "/os/planning", key: "planning", label: "Planning", icon: CalendarRange, always: true },
    { to: "/os/vetta", key: "vetta", label: t("os.nav.vetta"), icon: Boxes },
    { to: "/os/crm", key: "crm", label: t("os.nav.crm"), icon: Users },
    { to: "/os/operaciones", key: "operaciones", label: "Operaciones", icon: Wrench, always: true },
    { to: "/os/personas", key: "personas", label: t("os.nav.personas"), icon: Users, always: true },
    { to: "/os/core-hr", key: "corehr", label: t("os.nav.corehr"), icon: IdCard, always: true },
    { to: "/os/talent", key: "talent", label: t("os.nav.talent"), icon: Users2, always: true },
    { to: "/os/performance", key: "desempeno", label: t("os.nav.desempeno"), icon: Target, always: true },
    { to: "/os/learning", key: "aprendizaje", label: t("os.nav.aprendizaje"), icon: Lightbulb, always: true },
    { to: "/os/workforce", key: "workforce", label: t("os.nav.workforce"), icon: ClockIcon, always: true },
    { to: "/os/payroll", key: "nomina", label: t("os.nav.nomina"), icon: Wallet, always: true },
    { to: "/os/wellness", key: "bienestar", label: t("os.nav.bienestar"), icon: HeartPulse, always: true },
    { to: "/os/contabilidad", key: "contabilidad", label: "Finanzas", icon: Calculator },
    { to: "/os/facturacion", key: "facturacion", label: "Facturación CFDI", icon: FileCheck2, always: true },
    { to: "/os/shop-orders", key: "shop-orders", label: t("os.nav.shopOrders"), icon: Package },
    { to: "/os/automation", key: "automation", label: t("os.nav.automation"), icon: Zap },
    { to: "/os/intelligence", key: "intelligence", label: t("os.nav.intelligence"), icon: Activity },
    { to: "/os/assistant", key: "assistant", label: t("os.nav.assistant"), icon: Sparkles },
    { to: "/os/academia", key: "academy", label: t("os.nav.academy"), icon: GraduationCap, always: true },
    { to: "/os/chat", key: "chat", label: t("os.nav.chat"), icon: MessageCircle, always: true },
    { to: "/os/calendario", key: "calendario", label: "Calendario", icon: CalendarDays, always: true },
    { to: "/os/metricas-maestras", key: "master-metrics", label: "Métricas Maestras", icon: Trophy },
    { to: "/os/admin", key: "admin", label: t("os.nav.admin"), icon: Shield },
    { to: "/os/bitacora", key: "bitacora", label: t("os.nav.bitacora"), icon: ScrollText },
    { to: "/os/design", key: "design", label: t("os.nav.design"), icon: Palette },
    { to: "/os/workflows", key: "workflows", label: t("os.nav.workflows"), icon: GitBranch },
    { to: "/os/copiloto", key: "copiloto", label: t("os.nav.copiloto"), icon: Sparkles },
    { to: "/os/administracion", key: "administracion", label: t("os.nav.administracion"), icon: Shield },
    { to: "/os/marketplace", key: "marketplace", label: t("os.nav.marketplace"), icon: ShoppingBag },
    { to: "/os/extensiones", key: "extensiones", label: t("os.nav.extensiones"), icon: Puzzle },
    { to: "/os/integraciones", key: "integraciones", label: t("os.nav.integraciones"), icon: Zap },
    { to: "/os/plataforma", key: "plataforma", label: t("os.nav.plataforma"), icon: Activity },
  ];
  const NAV = ALL_NAV.filter((item) => (user?.role === "usuario_rd" ? canAccess(user?.role, item.key) : (item.always || canAccess(user?.role, item.key))))
    .filter((item) => !enabledModules || PROTECTED_NAV.has(item.key) || item.always || enabledModules.includes(item.key));
  const MODULES = [];

  useEffect(() => {
    if (user?.role === "usuario_rd" && !isPathAllowedForRD(location.pathname)) {
      navigate("/os", { replace: true });
    }
  }, [user?.role, location.pathname, navigate]);

  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");

  return (
    <div className="min-h-screen flex bg-cream">
      <DayStart />
      <aside className="hidden lg:flex flex-col w-64 bg-navy text-cream fixed inset-y-0 left-0 z-40">
        <div className="px-6 h-20 flex items-center border-b border-cream/10">
          <BrandMark tag={t("os.brandTag")} />
        </div>

        <div className="flex-1 min-h-0 flex flex-col pt-4">
          <CatalogNav />
        </div>

        <div className="p-3 border-t border-cream/10">
          <div className="flex items-center gap-3 px-3 py-3">
            <div className="w-9 h-9 rounded-full bg-gradient-violet flex items-center justify-center text-white text-xs font-medium">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-sm text-cream truncate">{user?.name}</div>
              <div className="text-xs text-cream/40 truncate">{user?.title || user?.role}</div>
            </div>
          </div>
          <button data-testid="os-logout" onClick={() => { logout(); navigate("/"); }}
            className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-cream/60 hover:text-cream hover:bg-cream/5 transition-colors">
            <LogOut className="w-4 h-4" /> {t("os.logout")}
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="os-mobile-drawer">
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} data-testid="os-mobile-overlay" />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85%] bg-navy text-cream flex flex-col animate-in slide-in-from-left duration-300">
            <div className="px-6 h-20 flex items-center justify-between border-b border-cream/10">
              <BrandMark onClick={() => setMobileOpen(false)} />
              <button data-testid="os-mobile-close" onClick={() => setMobileOpen(false)} className="text-cream/60 hover:text-cream"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 min-h-0 flex flex-col pt-4">
              <CatalogNav onNavigate={() => setMobileOpen(false)} />
            </nav>
            <div className="p-3 border-t border-cream/10">
              <div className="flex items-center gap-3 px-3 py-3">
                <div className="w-9 h-9 rounded-full bg-gradient-violet flex items-center justify-center text-white text-xs font-medium">{initials}</div>
                <div className="min-w-0">
                  <div className="text-sm text-cream truncate">{user?.name}</div>
                  <div className="text-xs text-cream/40 truncate">{user?.title || user?.role}</div>
                </div>
              </div>
              <button data-testid="os-mobile-logout" onClick={() => { setMobileOpen(false); logout(); navigate("/"); }}
                className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-cream/60 hover:text-cream hover:bg-cream/5 transition-colors">
                <LogOut className="w-4 h-4" /> {t("os.logout")}
              </button>
            </div>
          </aside>
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 h-20 backdrop-blur-xl bg-cream/80 border-b border-navy/5 flex items-center justify-between px-6 lg:px-10">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="os-mobile-menu-btn" onClick={() => setMobileOpen(true)} className="lg:hidden -ml-1 p-2 rounded-lg text-navy/60 hover:bg-navy/5 transition-colors">
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <div className="overline text-navy/40">OMNIA OS</div>
              <h1 className="text-xl font-medium tracking-tight truncate" data-testid="os-page-title">{title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <TenantSwitcher />
            <GlobalSearch />
            <LangToggle />
            <ThemeToggle />
            <ChatBell />
            <NotificationBell />
            <Link to="/os/omnia" data-testid="os-omnia-ai-link" className="hidden sm:inline-flex items-center gap-2 rounded-full bg-navy text-white text-sm px-4 py-2 hover:bg-navy-soft transition-colors">
              <Sparkles className="w-4 h-4" /> {t("os.askAI")}
            </Link>
          </div>
        </header>

        <main className="p-6 lg:p-10">{children}</main>
      </div>
    </div>
  );
}
